import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import {
  ArrowRight, Factory, Truck, Shield, MapPin,
  Building2, Award, Globe2, Package, Wrench, ClipboardCheck
} from "lucide-react";
import { Button } from "@/components/ui/button";
import ClientLogos from "@/components/ClientLogos";
import usePageSEOOverride from "@/hooks/useSEOOverrides";
import usePageContentOverrides from "@/hooks/usePageContentOverrides";

/**
 * MetroLandingPage — Dedicated pan-India / metro-cities landing page.
 * URL: /modular-office-furniture-manufacturer  (no city, non-location-bound)
 *
 * Purpose: Google Ads landing page for pan-India metro campaigns (Bengaluru,
 * Delhi NCR, Pune, Hyderabad, Chennai). Does NOT replace the Mumbai page —
 * kept fully independent so Speicher's Mumbai portal edits remain untouched.
 */

// ── Hero showcase images (auto-rotate) ──────────────────────────────────
const HERO_SHOWCASE = [
  {
    src: "https://customer-assets-eiarnc6j.emergentagent.net/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/b3qpym0u_image.png",
    alt: "Open-plan corporate office with modular workstations, phone booths and blue carpet — pan-India installations by Ezeefit Office Systems",
  },
  {
    src: "https://customer-assets-eiarnc6j.emergentagent.net/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/pa1nfi2j_image.png",
    alt: "Modular cubicle workstations with integrated storage, laptop and planter tops — Ezeefit corporate fit-out",
  },
  {
    src: "https://customer-assets-eiarnc6j.emergentagent.net/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/ud4fc2pq_image.png",
    alt: "Executive cabin with veneer desk, high-back leather chair and framed art — Ezeefit turnkey office interior",
  },
];

// ── Metro cities matrix (mirrors Google Ads campaign geo-targets) ────────
const METRO_CITIES = [
  { name: "Mumbai", alias: "MMR & Bhiwandi", note: "Home turf — our Bhiwandi (Thane) manufacturing facility handles same-week installation across Mumbai, Navi Mumbai and MMR." },
  { name: "Bengaluru", alias: "Bangalore", note: "Highest demand for IT parks, tech startups and MNC office expansions." },
  { name: "Delhi NCR", alias: "Gurugram & Noida", note: "Massive hub for corporate headquarters, financial institutions and large-scale commercial real estate." },
  { name: "Pune", alias: null, note: "Highly accessible from our Mumbai / Bhiwandi hub (reduced logistics friction) with high demand from IT and industrial sectors." },
  { name: "Hyderabad", alias: null, note: "Rapidly growing commercial office footprint — HITEC City and Financial District." },
  { name: "Chennai", alias: null, note: "Strong manufacturing, corporate and IT centre with sustained fit-out demand." },
  { name: "Kolkata", alias: null, note: "Eastern-corridor gateway for BFSI, IT and public-sector fit-outs; regular container-load dispatch from Bhiwandi." },
  { name: "Ahmedabad", alias: null, note: "Fast-growing corporate and manufacturing base in Gujarat with strong demand for modular workstation floors." },
];

const FAQS = [
  {
    q: "What is a modular office furniture manufacturer?",
    a: "A modular office furniture manufacturer designs, produces and installs factory-built, reconfigurable office furniture systems — including workstations, cubicles, acoustic phone booths, glass partitions, conference tables and executive desks — sized and finished to a client's floor plan. Ezeefit Office Systems manufactures the complete range in-house at our ISO 9001:2015 certified Bhiwandi (Thane) facility.",
  },
  {
    q: "Who is the best B2B modular office furniture manufacturer in India?",
    a: "Ezeefit Office Systems is a leading B2B modular office furniture manufacturer in India, operating since 2014, ISO 9001:2015 certified, with a dedicated Bhiwandi manufacturing facility and a proven 9-step execution process for pan-India corporate deployments across Mumbai, Bengaluru, Delhi NCR, Pune, Hyderabad, Chennai, Kolkata and Ahmedabad.",
  },
  {
    q: "Does Ezeefit Office Systems handle pan-India delivery and installation?",
    a: "Yes. We manufacture in-house at our Bhiwandi (Thane) factory and provide complete site assembly, logistics and turnkey installation services for corporate offices, IT parks and MNCs across all major metro cities in India — including Mumbai, Bengaluru, Delhi NCR, Pune, Hyderabad, Chennai, Kolkata and Ahmedabad.",
  },
  {
    q: "What types of modular office furniture do you manufacture?",
    a: "We design and manufacture 21 categories of commercial office furniture: linear workstations, corner workstations, hot desks, acoustic workstations, height-adjustable sit-stand desks, executive desks and chairs, meeting tables, meeting pods, meeting booths, acoustic phone booths, privacy screens, cubicle systems, glass partitions, modular wall partitions, reception counters, training-room furniture, filing cabinets, teller counters, customer seating and breakout furniture.",
  },
  {
    q: "How much does modular office furniture cost in India?",
    a: "Modular office furniture costs in India vary by product category, size, finish and order volume. As a direct factory manufacturer, Ezeefit Office Systems supplies workstations, phone booths and boardroom furniture at manufacturer pricing with no dealer margin. Every enquiry receives a detailed CAD layout and itemised quotation within 24-48 hours based on your floor plan and specifications.",
  },
  {
    q: "Can we customize furniture dimensions and materials for large project orders?",
    a: "Yes. As a direct factory manufacturer, we offer end-to-end customization on table-top dimensions, metal-frame powder-coating finishes, laminate and veneer shades, acoustic fabric colours, glass thickness and integrated wire-management systems. Every project ships with a CAD layout tailored to your floor plan.",
  },
  {
    q: "What is the minimum order size or lead time for corporate orders?",
    a: "We cater to corporate fit-outs of all sizes — from medium-scale startups to multi-floor enterprise spaces. Standard lead time for a typical 100-seater workstation project is 4-6 weeks from confirmed order, with initial CAD drawings provided within 24-48 hours of enquiry.",
  },
  {
    q: "How long does corporate office furniture installation take?",
    a: "For a typical 100-seater office fit-out, our on-site assembly team completes the workstation, phone-booth and boardroom installation in 5-7 working days after cargo delivery. Larger multi-floor deployments are executed in phased handovers to minimise business disruption for your client operations.",
  },
  {
    q: "Do you have ISO certification for your manufacturing?",
    a: "Yes. Ezeefit Office Systems is ISO 9001:2015 certified for quality management across the full manufacturing lifecycle — from raw material intake and CNC processing to powder coating, laminate press, quality inspection and on-site installation.",
  },
  {
    q: "Where is your factory located?",
    a: "Our modular office furniture manufacturing facility is located in Bhiwandi, Thane district (Maharashtra, India) — 40 km from Mumbai and directly connected to the national freight corridor. This enables full-container-load dispatch to any metro city in India within 3-5 days.",
  },
  {
    q: "How can I order custom modular office workstations in bulk for an MNC or IT park?",
    a: "Share your floor plan, seat count and requirement brief with our project team via the enquiry form. Within 24-48 hours you receive: (1) CAD layout with workstation, meeting-room and cabin placement, (2) itemised commercial quotation, (3) material and finish samples on request, and (4) a project timeline with dispatch and installation milestones.",
  },
];

// ── 9-Step manufacturing → installation process (HowTo schema-ready) ────
const PROCESS_STEPS = [
  { name: "Site Survey & Requirement Brief", desc: "On-site or virtual survey of your office floor plan, seat count, functional zones and finish preferences." },
  { name: "CAD Layout & 3D Visualisation", desc: "Detailed 2D layout and 3D renders showing workstation placement, meeting rooms, cabins, phone booths and circulation paths." },
  { name: "Commercial Quotation & Sampling", desc: "Itemised quotation with material samples — laminate shades, fabric colours, metal finishes and glass options." },
  { name: "Order Confirmation & Prototype Sign-off", desc: "Confirmed order with prototype approval for critical workstations or executive units before batch manufacturing." },
  { name: "In-House Manufacturing", desc: "CNC processing, edge banding, powder coating, fabric wrapping and QA inspection at our Bhiwandi (Thane) ISO 9001:2015 certified facility." },
  { name: "Pre-Dispatch Quality Check", desc: "Every unit is inspected, packed and photographed against the CAD spec before dispatch." },
  { name: "Pan-India Freight & Logistics", desc: "Full-container-load or LTL dispatch with real-time tracking to any metro corridor in India." },
  { name: "On-Site Assembly & Installation", desc: "Our in-house crew assembles workstations, glass partitions, acoustic pods and boardroom tables on-site with factory tooling." },
  { name: "Snag Closure & Formal Handover", desc: "Facility team walk-through, snag-list closure, cable-management routing and project handover with photo documentation." },
];

// ── JSON-LD schemas ──────────────────────────────────────────────────────
const CANONICAL_URL = "https://ezeefitofficesystems.in/modular-office-furniture-manufacturer";

const buildOrgSchema = () => JSON.stringify({
  "@context": "https://schema.org",
  "@type": ["Organization", "Manufacturer"],
  "@id": `${CANONICAL_URL}#org`,
  name: "Ezeefit Office Systems",
  legalName: "Ezeefit Modular Furniture Pvt. Ltd.",
  url: CANONICAL_URL,
  logo: "https://ezeefitofficesystems.in/media/logo-2026.png",
  image: HERO_SHOWCASE.map((h) => h.src),
  description: "ISO 9001:2015 certified B2B modular office furniture manufacturer with a dedicated Bhiwandi manufacturing facility. Direct factory supply and pan-India installation for IT parks, MNCs and corporate offices.",
  slogan: "Direct Factory Supply & Pan-India Setup",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Bhiwandi Manufacturing Facility",
    addressLocality: "Bhiwandi",
    addressRegion: "Thane, Maharashtra",
    addressCountry: "IN",
  },
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+91-9820596811",
    contactType: "sales",
    areaServed: "IN",
    availableLanguage: ["English", "Hindi", "Marathi"],
  },
  hasCredential: {
    "@type": "EducationalOccupationalCredential",
    credentialCategory: "ISO 9001:2015 Certification",
  },
  knowsAbout: [
    "Modular Office Workstations", "Acoustic Phone Booths", "Corporate Conference Tables",
    "Executive Desks", "Glass Partitions", "Cubicle Systems", "Height-Adjustable Desks",
    "Reception Counters", "Filing Cabinets", "Turnkey Office Fit-Out",
  ],
  areaServed: [
    { "@type": "City", name: "Mumbai" },
    { "@type": "City", name: "Bengaluru" },
    { "@type": "City", name: "Delhi NCR" },
    { "@type": "City", name: "Pune" },
    { "@type": "City", name: "Hyderabad" },
    { "@type": "City", name: "Chennai" },
    { "@type": "City", name: "Kolkata" },
    { "@type": "City", name: "Ahmedabad" },
    { "@type": "Country", name: "India" },
  ],
  foundingDate: "2014",
});

const buildLocalBusinessSchema = () => JSON.stringify({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": `${CANONICAL_URL}#localbusiness`,
  name: "Ezeefit Office Systems — Bhiwandi Manufacturing Facility",
  image: HERO_SHOWCASE[0].src,
  url: CANONICAL_URL,
  telephone: "+91-9820596811",
  priceRange: "₹₹",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Bhiwandi Manufacturing Facility",
    addressLocality: "Bhiwandi",
    addressRegion: "Maharashtra",
    postalCode: "421302",
    addressCountry: "IN",
  },
  geo: { "@type": "GeoCoordinates", latitude: 19.2967, longitude: 73.0631 },
  openingHoursSpecification: {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
    opens: "09:30",
    closes: "18:30",
  },
});

const buildServiceSchema = () => JSON.stringify({
  "@context": "https://schema.org",
  "@type": "Service",
  "@id": `${CANONICAL_URL}#service`,
  serviceType: "Modular Office Furniture Manufacturing & Turnkey Installation",
  provider: { "@id": `${CANONICAL_URL}#org` },
  areaServed: { "@type": "Country", name: "India" },
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Modular Office Furniture Catalog",
    itemListElement: [
      { "@type": "Offer", itemOffered: { "@type": "Product", name: "Modular Office Linear Workstations" } },
      { "@type": "Offer", itemOffered: { "@type": "Product", name: "Acoustic Phone Booths & Meeting Pods" } },
      { "@type": "Offer", itemOffered: { "@type": "Product", name: "Corporate Conference Tables" } },
      { "@type": "Offer", itemOffered: { "@type": "Product", name: "Executive Desks & Cabin Furniture" } },
      { "@type": "Offer", itemOffered: { "@type": "Product", name: "Glass Cabin Partitions" } },
      { "@type": "Offer", itemOffered: { "@type": "Product", name: "Height-Adjustable Sit-Stand Desks" } },
      { "@type": "Offer", itemOffered: { "@type": "Product", name: "Modular Cubicle Workstation Systems" } },
      { "@type": "Offer", itemOffered: { "@type": "Product", name: "Reception & Teller Counters" } },
    ],
  },
});

const buildHowToSchema = () => JSON.stringify({
  "@context": "https://schema.org",
  "@type": "HowTo",
  name: "How to order custom modular office furniture from Ezeefit Office Systems",
  description: "A 9-step turnkey process from site survey to installed office floor.",
  totalTime: "P30D",
  step: PROCESS_STEPS.map((s, i) => ({
    "@type": "HowToStep",
    position: i + 1,
    name: s.name,
    text: s.desc,
  })),
});

const buildItemListSchema = () => JSON.stringify({
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "Metro cities serviced by Ezeefit Office Systems",
  itemListElement: METRO_CITIES.map((c, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: c.name,
    description: c.note,
  })),
});

const buildFaqSchema = () => JSON.stringify({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map(({ q, a }) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", text: a },
  })),
  speakable: {
    "@type": "SpeakableSpecification",
    cssSelector: ["h1", "[data-speakable='true']"],
  },
});

const buildBreadcrumbSchema = () => JSON.stringify({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://ezeefitofficesystems.in" },
    { "@type": "ListItem", position: 2, name: "Modular Office Furniture Manufacturer", item: CANONICAL_URL },
  ],
});

const buildWebPageSchema = () => JSON.stringify({
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": CANONICAL_URL,
  url: CANONICAL_URL,
  name: "Modular Office Furniture Manufacturer | Direct Factory Supply India",
  description: "ISO 9001:2015 certified modular office furniture manufacturer. Custom workstations, acoustic phone booths & conference tables. Direct factory setup across India.",
  isPartOf: { "@type": "WebSite", name: "Ezeefit Office Systems", url: "https://ezeefitofficesystems.in" },
  primaryImageOfPage: HERO_SHOWCASE[0].src,
  speakable: {
    "@type": "SpeakableSpecification",
    cssSelector: ["h1", "[data-testid='metro-hero-delivery']", "[data-speakable='true']"],
  },
  about: { "@id": `${CANONICAL_URL}#org` },
});

// ── Trust badge ─────────────────────────────────────────────────────────
const TrustBadge = ({ Icon, title, body }) => (
  <div className="flex flex-col items-start gap-3 p-6 border border-[#E2E8F0] bg-white">
    <div className="w-11 h-11 flex items-center justify-center bg-[#0F172A] text-[#F97316]">
      <Icon className="w-5 h-5" strokeWidth={1.5} />
    </div>
    <h3 className="text-base font-semibold text-[#0F172A]">{title}</h3>
    <p className="text-sm text-[#475569] leading-relaxed">{body}</p>
  </div>
);

// ── Hero showcase: 3-image crossfade carousel (auto-rotates every 4.5s) ──
const HeroShowcase = () => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % HERO_SHOWCASE.length), 4500);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="border border-white/10 shadow-2xl overflow-hidden bg-[#0F172A]" data-testid="metro-hero-showcase">
      <div className="px-6 lg:px-8 py-5 border-b border-white/10">
        <p className="text-[10px] font-mono tracking-widest uppercase text-[#F97316] mb-1.5">Direct Factory Pricing &nbsp;|&nbsp; No Dealer Markups</p>
        <p className="text-white text-sm lg:text-base leading-snug">
          Across <strong>Mumbai, Bengaluru, Hyderabad, Delhi NCR, Pune, Chennai, Kolkata, Ahmedabad</strong> &amp; pan-India.
        </p>
      </div>
      <div className="relative aspect-[5/3] w-full overflow-hidden bg-[#0F172A]">
        {HERO_SHOWCASE.map((img, i) => (
          <img
            key={img.src}
            src={img.src}
            alt={img.alt}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-in-out ${i === idx ? "opacity-100" : "opacity-0"}`}
            loading={i === 0 ? "eager" : "lazy"}
            fetchpriority={i === 0 ? "high" : "auto"}
            decoding="async"
            width="900"
            height="540"
          />
        ))}
        {/* Slide indicators */}
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2 z-10" data-testid="metro-hero-dots">
          {HERO_SHOWCASE.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Show image ${i + 1}`}
              onClick={() => setIdx(i)}
              className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-[#F97316]" : "w-1.5 bg-white/40 hover:bg-white/70"}`}
              data-testid={`metro-hero-dot-${i}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

// ── India service-corridor map ──────────────────────────────────────────
// Stylised SVG India outline with plotted metro service hubs. All coords are
// hand-tuned to a 400×460 viewBox that roughly follows the Indian landmass.
// The Bhiwandi (Mumbai) hub is highlighted as the manufacturing origin.
const MAP_HUBS = [
  { key: "delhi",     name: "Delhi NCR",   x: 172, y: 98,  origin: false },
  { key: "ahmedabad", name: "Ahmedabad",   x: 120, y: 185, origin: false },
  { key: "kolkata",   name: "Kolkata",     x: 265, y: 205, origin: false },
  { key: "mumbai",    name: "Mumbai / Bhiwandi", x: 128, y: 232, origin: true },
  { key: "pune",      name: "Pune",        x: 142, y: 250, origin: false },
  { key: "hyderabad", name: "Hyderabad",   x: 192, y: 285, origin: false },
  { key: "bengaluru", name: "Bengaluru",   x: 180, y: 330, origin: false },
  { key: "chennai",   name: "Chennai",     x: 220, y: 340, origin: false },
];

const IndiaServiceMap = () => (
  <svg viewBox="0 0 400 460" className="w-full h-auto" aria-label="Pan-India service corridors from Bhiwandi factory">
    {/* Rough India outline path — stylised, not geographically exact */}
    <path
      d="M 175 30 L 200 42 L 235 40 L 262 60 L 258 85 L 240 95 L 236 115 L 250 132 L 275 145
         L 305 160 L 315 190 L 300 215 L 285 235 L 270 260 L 265 285 L 250 310 L 238 335
         L 220 360 L 210 385 L 195 400 L 178 385 L 165 355 L 155 325 L 148 295 L 138 268
         L 122 245 L 110 220 L 100 195 L 95 170 L 105 145 L 118 125 L 130 105 L 145 85
         L 158 62 L 170 45 Z"
      fill="#F1F5F9"
      stroke="#CBD5E1"
      strokeWidth="1.5"
    />
    {/* Corridors — thin orange lines from Mumbai (Bhiwandi) hub to each metro */}
    {MAP_HUBS.filter((h) => !h.origin).map((h) => {
      const origin = MAP_HUBS.find((m) => m.origin);
      return (
        <line
          key={`route-${h.key}`}
          x1={origin.x}
          y1={origin.y}
          x2={h.x}
          y2={h.y}
          stroke="#F97316"
          strokeWidth="1"
          strokeDasharray="3 3"
          opacity="0.5"
        />
      );
    })}
    {/* City dots + labels */}
    {MAP_HUBS.map((h) => (
      <g key={h.key}>
        {h.origin ? (
          <>
            {/* Pulsing ring on origin */}
            <circle cx={h.x} cy={h.y} r="12" fill="#F97316" opacity="0.15" />
            <circle cx={h.x} cy={h.y} r="7" fill="#F97316" opacity="0.35" />
            <circle cx={h.x} cy={h.y} r="4" fill="#F97316" />
          </>
        ) : (
          <>
            <circle cx={h.x} cy={h.y} r="6" fill="#FFFFFF" stroke="#F97316" strokeWidth="1.5" />
            <circle cx={h.x} cy={h.y} r="2.5" fill="#F97316" />
          </>
        )}
        <text
          x={h.x + 10}
          y={h.y + 4}
          fontSize="11"
          fill="#0F172A"
          fontWeight={h.origin ? "700" : "600"}
          fontFamily="system-ui, sans-serif"
        >
          {h.name}
        </text>
      </g>
    ))}
  </svg>
);

export default function MetroLandingPage() {
  usePageSEOOverride("/modular-office-furniture-manufacturer", {
    title: "Modular Office Furniture Manufacturer | Direct Factory Supply India",
    canonical: "https://ezeefitofficesystems.in/modular-office-furniture-manufacturer",
    description: "ISO 9001:2015 certified modular office furniture manufacturer. Custom workstations, acoustic phone booths & conference tables. Direct factory setup across India.",
  });
  usePageContentOverrides("/modular-office-furniture-manufacturer");

  // Imperatively set title + description because react-helmet is unreliable in this
  // codebase (index.html <title> often wins). The SEO agency portal may later replace
  // these via the page-override DB row — that path is handled by usePageSEOOverride.
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Modular Office Furniture Manufacturer | Direct Factory Supply India";
    return () => { document.title = prevTitle; };
  }, []);

  return (
    <div className="pt-20" data-testid="metro-landing-page">
      <Helmet>
        <title>Modular Office Furniture Manufacturer | Direct Factory Supply India</title>
        <meta name="description" content="ISO 9001:2015 certified modular office furniture manufacturer. Custom workstations, acoustic phone booths & conference tables. Direct factory setup across India." />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="keywords" content="modular office furniture manufacturer, corporate office furniture supplier india, B2B modular workstations, acoustic phone booths, conference tables manufacturer, factory direct commercial workstations, ISO certified office furniture, turnkey office interior setup, bulk office furniture supplier india, custom modular workstations, executive desks, glass cabin partitions, height adjustable desks" />
        <meta name="author" content="Ezeefit Office Systems" />
        <meta name="geo.region" content="IN-MH" />
        <meta name="geo.placename" content="Bhiwandi, Thane, Maharashtra" />
        <meta name="geo.position" content="19.2967;73.0631" />
        <meta name="ICBM" content="19.2967, 73.0631" />
        <link rel="canonical" href="https://ezeefitofficesystems.in/modular-office-furniture-manufacturer" />
        <link rel="alternate" hrefLang="en-in" href="https://ezeefitofficesystems.in/modular-office-furniture-manufacturer" />
        <link rel="alternate" hrefLang="x-default" href="https://ezeefitofficesystems.in/modular-office-furniture-manufacturer" />
        {/* OpenGraph */}
        <meta property="og:title" content="Modular Office Furniture Manufacturer | Direct Factory Supply India" />
        <meta property="og:description" content="Direct factory office furniture manufacturer serving Bengaluru, Delhi NCR, Pune, Hyderabad, Chennai, Kolkata, Ahmedabad, Mumbai and pan-India MNCs. ISO 9001:2015 certified since 2014." />
        <meta property="og:url" content="https://ezeefitofficesystems.in/modular-office-furniture-manufacturer" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={HERO_SHOWCASE[0].src} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="720" />
        <meta property="og:locale" content="en_IN" />
        <meta property="og:site_name" content="Ezeefit Office Systems" />
        {/* Twitter Cards */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Modular Office Furniture Manufacturer | Direct Factory Supply India" />
        <meta name="twitter:description" content="ISO 9001:2015 certified modular office furniture manufacturer. Direct factory supply across pan-India metros." />
        <meta name="twitter:image" content={HERO_SHOWCASE[0].src} />
      </Helmet>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: buildWebPageSchema() }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: buildOrgSchema() }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: buildLocalBusinessSchema() }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: buildServiceSchema() }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: buildHowToSchema() }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: buildItemListSchema() }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: buildFaqSchema() }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: buildBreadcrumbSchema() }} />

      {/* ── HERO ─────────────────────────────────────────────────────── */}
      <section className="relative bg-[#0F172A] text-white overflow-hidden" data-testid="metro-hero">
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
          backgroundImage: "radial-gradient(circle at 20% 20%, #F97316 0%, transparent 40%), radial-gradient(circle at 80% 60%, #F97316 0%, transparent 40%)",
        }} />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-10 lg:py-28">
          <div className="grid lg:grid-cols-[1.35fr_1fr] gap-8 lg:gap-20 items-start">
            {/* Left column: hero copy */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="order-2 lg:order-1"
            >
              <span
                className="inline-block px-3 py-1.5 border border-[#F97316]/40 text-[10px] font-mono tracking-widest uppercase text-[#F97316] mb-6"
                data-testid="metro-hero-pill"
              >
                Pan-India Fulfilment · Direct Factory Pricing · Since 2014
              </span>
              <h1
                className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight mb-6"
                data-testid="metro-hero-h1"
              >
                Modular Office Furniture Manufacturer
                <span className="block text-[#F97316] mt-3 text-3xl sm:text-4xl lg:text-5xl">
                  Direct Factory Supply & Pan-India Setup
                </span>
              </h1>
              {/* Delivery footprint — desktop only (mobile shows this inside the hero image caption). */}
              <div className="hidden lg:block mt-8 mb-8 border-l-2 border-[#F97316] pl-4 max-w-2xl" data-testid="metro-hero-delivery">
                <p className="text-[10px] font-mono tracking-widest uppercase text-[#F97316] mb-1.5">Delivered &amp; Installed Complete Modular Office Furniture Setup</p>
                <p className="text-white text-sm lg:text-base leading-snug">
                  Across <strong>Mumbai, Bengaluru, Hyderabad, Delhi NCR, Pune, Chennai, Kolkata, Ahmedabad</strong> &amp; pan-India.
                </p>
              </div>
              <p className="text-base lg:text-lg text-[#94A3B8] leading-relaxed max-w-2xl mb-4">
                Ezeefit Office Systems is a leading <strong className="text-white">B2B modular office furniture manufacturer</strong>. From our state-of-the-art Bhiwandi (Thane) facility we design, manufacture and install complete office interiors for <strong className="text-white">IT companies, MNCs and corporate spaces</strong> across India.
              </p>
              <p className="text-base text-[#94A3B8] leading-relaxed max-w-2xl mb-10">
                ISO 9001:2015 certified quality management with a proven <Link to="/process" className="text-[#F97316] underline underline-offset-4">9-step execution process</Link> — trusted by procurement teams in Bengaluru, Delhi NCR, Pune, Hyderabad, Chennai and beyond.
              </p>

              {/* CTAs */}
              <div className="flex flex-wrap items-center gap-4">
                <Button
                  asChild
                  className="bg-[#F97316] hover:bg-orange-600 text-white rounded-none px-8 py-6 font-semibold uppercase tracking-wider"
                  data-testid="metro-hero-cta-quote"
                >
                  <Link to="/contact?intent=pan-india-quote">
                    Request Project Quote
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Link>
                </Button>
              </div>

              {/* Quick-jump anchors */}
              <div className="mt-10 flex flex-wrap gap-x-6 gap-y-2 text-[13px]" data-testid="metro-quick-jumps">
                <a href="#workstations" className="text-[#94A3B8] hover:text-[#F97316] transition-colors">→ Workstations</a>
                <a href="#phone-booths" className="text-[#94A3B8] hover:text-[#F97316] transition-colors">→ Phone Booths</a>
                <a href="#conference-tables" className="text-[#94A3B8] hover:text-[#F97316] transition-colors">→ Conference Tables</a>
                <a href="#factory-setup" className="text-[#94A3B8] hover:text-[#F97316] transition-colors">→ Factory Setup</a>
                <a href="#faqs" className="text-[#94A3B8] hover:text-[#F97316] transition-colors">→ FAQs</a>
              </div>
            </motion.div>

            {/* Right column: auto-rotating 3-image showcase + trust stats
                On mobile shows FIRST (order-1) so Google-Ads visitors see the visual instantly. */}
            <motion.aside
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="lg:pt-4 space-y-6 order-1 lg:order-2"
              data-testid="metro-hero-image"
            >
              <HeroShowcase />
              {/* Trust-stats strip — desktop only (keeps mobile hero compact) */}
              <div className="hidden lg:grid grid-cols-2 gap-3" data-testid="metro-hero-stats">
                {[
                  { v: "Since 2014", l: "Manufacturing" },
                  { v: "ISO 9001:2015", l: "Certified quality" },
                  { v: "21 Categories", l: "Full product range" },
                  { v: "Pan-India", l: "Shipping & install" },
                ].map((s) => (
                  <div key={s.v} className="p-4 border border-white/10 bg-white/[0.03] hover:border-[#F97316]/50 hover:bg-white/[0.06] transition-colors">
                    <div className="text-white text-lg font-semibold leading-tight">{s.v}</div>
                    <div className="text-[#94A3B8] text-xs mt-1 leading-tight">{s.l}</div>
                  </div>
                ))}
              </div>
              {/* Metro-cities microline for SEO/GEO intent capture — desktop only */}
              <div className="hidden lg:block pt-2 text-xs text-[#64748B] leading-relaxed">
                <span className="text-[#F97316] font-mono uppercase tracking-widest text-[10px] mr-2">Cities Serviced</span>
                Mumbai · Bengaluru · Delhi NCR · Pune · Hyderabad · Chennai · Kolkata · Ahmedabad
              </div>
            </motion.aside>
          </div>
        </div>
      </section>

      {/* ── DEFINITION BLOCK (featured-snippet magnet for AEO) ────────── */}
      <section className="py-14 lg:py-20 bg-white border-b border-[#E2E8F0]" data-testid="metro-definition">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24">
          <span className="font-mono text-xs text-[#F97316] tracking-widest uppercase mb-3 block">Definition · Quick Answer</span>
          <h2 className="text-2xl lg:text-3xl font-bold text-[#0F172A] mb-5 tracking-tight" id="what-is-modular-office-furniture-manufacturer">
            What is a modular office furniture manufacturer?
          </h2>
          <p className="text-[#0F172A] text-lg leading-relaxed mb-4" data-speakable="true">
            A <strong>modular office furniture manufacturer</strong> is a company that designs, produces and installs factory-built, reconfigurable office furniture systems — including <strong>workstations, cubicles, acoustic phone booths, glass partitions, conference tables and executive desks</strong> — sized and finished to a client's floor plan. Ezeefit Office Systems manufactures the complete 21-category range in-house at its <strong>ISO 9001:2015 certified Bhiwandi (Thane) facility</strong> and installs pan-India through dedicated on-site assembly teams.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
            {[
              { k: "Founded", v: "2014" },
              { k: "Certification", v: "ISO 9001:2015" },
              { k: "Factory", v: "Bhiwandi (Thane)" },
              { k: "Categories", v: "21" },
              { k: "Service area", v: "Pan-India" },
              { k: "Lead time (CAD)", v: "24-48 hours" },
              { k: "Installation crew", v: "In-house" },
              { k: "Pricing model", v: "Direct factory" },
            ].map((f) => (
              <div key={f.k} className="p-4 bg-[#F8FAFC] border border-[#E2E8F0]">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#64748B]">{f.k}</div>
                <div className="text-base font-semibold text-[#0F172A] mt-1">{f.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ENTITY / TRUST BLOCK (AEO + GEO signals) ─────────────────── */}
      <section id="factory-setup" className="py-16 lg:py-24 bg-white" data-testid="metro-trust-block">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            <div>
              <span className="font-mono text-xs text-[#F97316] tracking-widest uppercase mb-3 block">Verified Manufacturing Entity</span>
              <h2 className="text-3xl lg:text-4xl font-bold text-[#0F172A] mb-6 tracking-tight">
                A direct factory. Not a reseller.
              </h2>
              <p className="text-[#475569] leading-relaxed mb-4">
                Ezeefit Office Systems operates a <strong>dedicated manufacturing facility in Bhiwandi, Thane</strong>, specializing in <strong>modular office workstations, acoustic pods and boardroom furniture</strong>. Every unit ships direct from our factory — no dealer margins, no re-branded imports.
              </p>
              <p className="text-[#475569] leading-relaxed mb-4">
                <strong>ISO 9001:2015 certified quality management</strong> with a <strong>9-step execution process</strong> — from CAD layouts to final on-site handover. Our procurement partners include IT parks, banks, BPOs and MNCs across India.
              </p>
              <div className="mt-8 grid grid-cols-2 gap-6">
                <div>
                  <div className="text-3xl font-bold text-[#0F172A]">2014</div>
                  <div className="text-sm text-[#64748B] mt-1">Manufacturing since</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-[#0F172A]">ISO 9001:2015</div>
                  <div className="text-sm text-[#64748B] mt-1">Certified quality</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-[#0F172A]">21</div>
                  <div className="text-sm text-[#64748B] mt-1">Product categories</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-[#0F172A]">Pan-India</div>
                  <div className="text-sm text-[#64748B] mt-1">Shipping & installation</div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <TrustBadge Icon={Factory} title="In-House Manufacturing" body="CNC borers, edge banders, panel saws and laminate press equipment — every component built inside our Bhiwandi plant." />
              <TrustBadge Icon={Truck} title="Pan-India Logistics" body="Dedicated freight partners for full-container-load deployments to Bengaluru, Delhi NCR, Pune, Hyderabad, Chennai and beyond." />
              <TrustBadge Icon={Shield} title="ISO 9001:2015 Certified" body="International standards for durability and repeatable build quality on every project, from a 20-seater to a 2,000-seater floor." />
              <TrustBadge Icon={Award} title="Direct Factory Pricing" body="No dealer margins. Custom workstation dimensions, material grades and powder-coating finishes at manufacturer cost." />
            </div>
          </div>
        </div>
      </section>

      {/* ── PAN-INDIA EXECUTION PROOF (map + metros + delivery proof) ── */}
      <section className="py-16 lg:py-24 bg-[#F8FAFC] border-y border-[#E2E8F0]" data-testid="metro-pan-india">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center gap-3 mb-3">
            <Globe2 className="w-5 h-5 text-[#F97316]" />
            <span className="font-mono text-xs text-[#F97316] tracking-widest uppercase">Pan-India Execution Proof</span>
          </div>
          <h2 className="text-3xl lg:text-4xl font-bold text-[#0F172A] mb-4 tracking-tight max-w-3xl">
            Delivered & installed across Mumbai, Bengaluru, Hyderabad, Delhi NCR, Pune, Chennai, Kolkata, Ahmedabad & pan-India
          </h2>
          <p className="text-[#475569] leading-relaxed max-w-3xl mb-12">
            Our Bhiwandi (Thane) manufacturing hub ships full-container-load workstation projects direct to your site — with dedicated on-site assembly teams for every major metro corridor in India.
          </p>

          {/* Map + Metro cards side-by-side */}
          <div className="grid lg:grid-cols-[1fr_1.35fr] gap-8 lg:gap-12 items-start">
            {/* Left: India map visual with plotted metros */}
            <div className="bg-white border border-[#E2E8F0] p-6 lg:p-8 lg:sticky lg:top-24" data-testid="metro-india-map">
              <p className="text-[10px] font-mono tracking-widest uppercase text-[#F97316] mb-4">Servicing Corridors</p>
              <IndiaServiceMap />
              <div className="mt-6 pt-6 border-t border-[#E2E8F0] flex items-center gap-3 text-xs text-[#64748B]">
                <span className="inline-block w-2 h-2 bg-[#F97316] rounded-full" />
                <span>Metro hubs actively serviced from Bhiwandi factory</span>
              </div>
            </div>

            {/* Right: city cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {METRO_CITIES.map((c) => (
                <div key={c.name} className="p-5 bg-white border border-[#E2E8F0] hover:border-[#F97316] transition-colors" data-testid={`metro-city-${c.name.toLowerCase().replace(/\s+/g, '-').replace(/&/g,'and')}`}>
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-[#F97316] shrink-0 mt-0.5" />
                    <div>
                      <h3 className="text-base lg:text-lg font-semibold text-[#0F172A] leading-tight">
                        {c.name}
                        {c.alias && <span className="text-[#64748B] font-normal text-sm"> ({c.alias})</span>}
                      </h3>
                      <p className="text-sm text-[#64748B] mt-1.5 leading-relaxed">{c.note}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── PAN-INDIA SHIPPING / SITE ASSEMBLY / INSTALLATION ─────────── */}
      <section className="py-16 lg:py-24 bg-white" data-testid="metro-shipping-block">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center gap-3 mb-3">
            <Truck className="w-5 h-5 text-[#F97316]" />
            <span className="font-mono text-xs text-[#F97316] tracking-widest uppercase">Turnkey Capability</span>
          </div>
          <h2 className="text-3xl lg:text-4xl font-bold text-[#0F172A] mb-4 tracking-tight max-w-4xl">
            Pan-India Shipping, Site Assembly & Installation Capabilities
          </h2>
          <p className="text-[#475569] leading-relaxed max-w-3xl mb-12">
            One team, one accountability line — from factory dispatch to final on-site handover. No third-party dealers, no assembly hand-offs.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="p-6 border border-[#E2E8F0] hover:border-[#F97316] transition-colors" data-testid="metro-cap-shipping">
              <Package className="w-6 h-6 text-[#F97316] mb-4" strokeWidth={1.5} />
              <h3 className="text-lg font-semibold text-[#0F172A] mb-2">Full-Container-Load Shipping</h3>
              <p className="text-sm text-[#475569] leading-relaxed">
                Dedicated freight partners handle FCL and LTL movement from our Bhiwandi factory to any metro corridor in India. Insured cargo, real-time tracking and consolidated delivery for multi-floor projects.
              </p>
            </div>
            <div className="p-6 border border-[#E2E8F0] hover:border-[#F97316] transition-colors" data-testid="metro-cap-assembly">
              <Wrench className="w-6 h-6 text-[#F97316] mb-4" strokeWidth={1.5} />
              <h3 className="text-lg font-semibold text-[#0F172A] mb-2">Trained On-Site Assembly Teams</h3>
              <p className="text-sm text-[#475569] leading-relaxed">
                Our in-house assembly crews travel to your site with factory tooling — workstations, glass partitions, acoustic pods and conference tables are installed to spec, not left in cartons.
              </p>
            </div>
            <div className="p-6 border border-[#E2E8F0] hover:border-[#F97316] transition-colors" data-testid="metro-cap-installation">
              <ClipboardCheck className="w-6 h-6 text-[#F97316] mb-4" strokeWidth={1.5} />
              <h3 className="text-lg font-semibold text-[#0F172A] mb-2">Turnkey Installation & Handover</h3>
              <p className="text-sm text-[#475569] leading-relaxed">
                Complete site setup — layout marking, unit assembly, cable-management routing, snag-list closure and formal handover with project photos. Facility teams sign off, we depart.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── CORPORATE CLIENT LOGOS ──────────────────────────────────── */}
      <div data-testid="metro-clientele-block">
        <div className="text-center pt-16 lg:pt-20 px-6 bg-[#F8FAFC]">
          <span className="font-mono text-xs text-[#F97316] tracking-widest uppercase mb-3 block">Trusted Nationwide</span>
          <h2 className="text-3xl lg:text-4xl font-bold text-[#0F172A] tracking-tight max-w-3xl mx-auto">
            Corporate clients we've delivered & installed for
          </h2>
        </div>
        <ClientLogos />
      </div>

      {/* ── PRODUCT ANCHORS — Workstations / Phone Booths / Conference Tables ── */}
      <section className="py-16 lg:py-24 bg-white" data-testid="metro-product-anchors">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 space-y-16">

          <div id="workstations" className="grid lg:grid-cols-2 gap-8 lg:gap-14 items-center">
            <div className="lg:order-1">
              <div className="aspect-[4/3] overflow-hidden border border-[#E2E8F0] bg-[#F8FAFC]">
                <picture style={{ display: "contents" }}>
                  <source srcSet="/media/landing/workstations.webp" type="image/webp" />
                  <img
                    src="/media/landing/workstations.jpg"
                    alt="Custom modular office workstations manufactured by Ezeefit — linear, corner and acoustic desk systems"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                    width="800"
                    height="600"
                    data-testid="metro-product-img-workstations"
                  />
                </picture>
              </div>
            </div>
            <div className="lg:order-2">
              <span className="font-mono text-xs text-[#F97316] tracking-widest uppercase mb-3 block">Product Line</span>
              <h2 className="text-3xl lg:text-4xl font-bold text-[#0F172A] tracking-tight mb-5">Custom Workstations & Modular Office Systems</h2>
              <p className="text-[#475569] leading-relaxed mb-5">
                <strong>Factory-direct commercial workstations</strong> engineered for high-density IT parks, BPO floors and corporate campuses. Choose linear clusters, acoustic pods, height-adjustable sit-stand desks or L-shaped corner workstations — every dimension is customisable to your CAD layout.
              </p>
              <div className="flex flex-wrap gap-2">
                {["linear-workstations", "corner-workstations", "acoustic-workstations", "height-adjustable-desks", "hot-desks"].map((s) => (
                  <Link key={s} to={`/products/${s}`} state={{ fromProducts: true }} className="text-sm text-[#0F172A] border border-[#E2E8F0] hover:border-[#F97316] hover:text-[#F97316] px-3 py-1.5 transition-colors">
                    {s.split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <div id="phone-booths" className="grid lg:grid-cols-2 gap-8 lg:gap-14 items-center border-t border-[#E2E8F0] pt-16">
            <div className="lg:order-2">
              <div className="aspect-[4/3] overflow-hidden border border-[#E2E8F0] bg-[#F8FAFC]">
                <picture style={{ display: "contents" }}>
                  <source srcSet="/media/landing/phone-booths.webp" type="image/webp" />
                  <img
                    src="/media/landing/phone-booths.jpg"
                    alt="Acoustic phone booths and meeting pods manufactured by Ezeefit — free-standing units with full glazing and integrated ventilation"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                    width="800"
                    height="600"
                    data-testid="metro-product-img-booths"
                  />
                </picture>
              </div>
            </div>
            <div className="lg:order-1">
              <span className="font-mono text-xs text-[#F97316] tracking-widest uppercase mb-3 block">Product Line</span>
              <h2 className="text-3xl lg:text-4xl font-bold text-[#0F172A] tracking-tight mb-5">Acoustic Meeting Pods & Phone Booths</h2>
              <p className="text-[#475569] leading-relaxed mb-5">
                Free-standing <strong>acoustic phone booths and meeting pods</strong> for open-plan floors that need quiet, distraction-free zones. Full glazing, fabric-wrapped acoustic panels, integrated ventilation and dimmable LED — assembled on-site anywhere in India.
              </p>
              <div className="flex flex-wrap gap-2">
                {["phone-booths", "meeting-pods", "meeting-booths", "privacy-screens"].map((s) => (
                  <Link key={s} to={`/products/${s}`} state={{ fromProducts: true }} className="text-sm text-[#0F172A] border border-[#E2E8F0] hover:border-[#F97316] hover:text-[#F97316] px-3 py-1.5 transition-colors">
                    {s.split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <div id="conference-tables" className="grid lg:grid-cols-2 gap-8 lg:gap-14 items-center border-t border-[#E2E8F0] pt-16">
            <div className="lg:order-1">
              <div className="aspect-[4/3] overflow-hidden border border-[#E2E8F0] bg-[#F8FAFC]">
                <picture style={{ display: "contents" }}>
                  <source srcSet="/media/landing/conference-tables.webp" type="image/webp" />
                  <img
                    src="/media/landing/conference-tables.jpg"
                    alt="Corporate boardroom conference tables manufactured by Ezeefit — laminate, veneer and solid-surface finishes with integrated cable management"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                    width="800"
                    height="600"
                    data-testid="metro-product-img-tables"
                  />
                </picture>
              </div>
            </div>
            <div className="lg:order-2">
              <span className="font-mono text-xs text-[#F97316] tracking-widest uppercase mb-3 block">Product Line</span>
              <h2 className="text-3xl lg:text-4xl font-bold text-[#0F172A] tracking-tight mb-5">Corporate Conference Tables Manufacturer</h2>
              <p className="text-[#475569] leading-relaxed mb-5">
                <strong>Boardroom and meeting tables</strong> in laminate, veneer and solid-surface finishes — sized from 6-seater huddle tables to 24-seater boardroom decks with integrated cable-management and AV cut-outs.
              </p>
              <div className="flex flex-wrap gap-2">
                {["meeting-tables", "executive-desks", "reception-counters", "training-room-setup"].map((s) => (
                  <Link key={s} to={`/products/${s}`} state={{ fromProducts: true }} className="text-sm text-[#0F172A] border border-[#E2E8F0] hover:border-[#F97316] hover:text-[#F97316] px-3 py-1.5 transition-colors">
                    {s.split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")}
                  </Link>
                ))}
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ── 9-STEP PROCESS (HowTo schema-mapped for AEO/GEO) ────────── */}
      <section className="py-16 lg:py-24 bg-[#F8FAFC] border-y border-[#E2E8F0]" data-testid="metro-process">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <span className="font-mono text-xs text-[#F97316] tracking-widest uppercase mb-3 block">Our 9-Step Turnkey Process</span>
          <h2 className="text-3xl lg:text-4xl font-bold text-[#0F172A] mb-4 tracking-tight max-w-4xl" id="how-to-order-modular-office-furniture">
            How to order custom modular office furniture from Ezeefit
          </h2>
          <p className="text-[#475569] leading-relaxed max-w-3xl mb-12" data-speakable="true">
            A single accountability line from your first enquiry to installed office floor — no dealer hand-offs, no sub-contracted assembly. Here is exactly what happens after you contact us.
          </p>
          <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="metro-process-steps">
            {PROCESS_STEPS.map((s, i) => (
              <li key={s.name} className="p-6 bg-white border border-[#E2E8F0] hover:border-[#F97316] transition-colors relative">
                <span className="absolute -top-3 -left-3 w-10 h-10 flex items-center justify-center bg-[#F97316] text-white font-bold text-sm rounded-full shadow-md">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="text-base font-semibold text-[#0F172A] mb-2 mt-2">{s.name}</h3>
                <p className="text-sm text-[#475569] leading-relaxed">{s.desc}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ── WHY CHOOSE US ─────────────────────────────────────────── */}
      <section className="py-16 lg:py-24 bg-[#0F172A] text-white" data-testid="metro-why-us">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <span className="font-mono text-xs text-[#F97316] tracking-widest uppercase mb-3 block">Why Corporate Procurement Teams Choose Ezeefit</span>
          <h2 className="text-3xl lg:text-4xl font-bold tracking-tight mb-12 max-w-3xl">
            Why Choose Ezeefit Office Systems as Your Manufacturing Partner
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { Icon: Factory, t: "Direct Factory Supply", b: "In-house manufacturing at our Bhiwandi (Thane) facility — no dealer markups, no re-branded imports." },
              { Icon: Shield, t: "ISO 9001:2015 Certified", b: "International quality management standards on every project, from CAD to handover." },
              { Icon: Truck, t: "Pan-India Fulfilment", b: "Full-container-load logistics with dedicated site-assembly teams for every metro corridor." },
              { Icon: Building2, t: "Turnkey Corporate Fit-Outs", b: "9-step execution: site survey → CAD → prototyping → manufacturing → freight → assembly → handover." },
            ].map(({ Icon, t, b }) => (
              <div key={t} className="p-6 border border-white/10 hover:border-[#F97316] transition-colors">
                <Icon className="w-6 h-6 text-[#F97316] mb-4" strokeWidth={1.5} />
                <h3 className="text-lg font-semibold mb-2">{t}</h3>
                <p className="text-sm text-[#94A3B8] leading-relaxed">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQs (schema-ready) ───────────────────────────────────── */}
      <section id="faqs" className="py-16 lg:py-24 bg-white" data-testid="metro-faqs">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24">
          <span className="font-mono text-xs text-[#F97316] tracking-widest uppercase mb-3 block">Frequently Asked Questions</span>
          <h2 className="text-3xl lg:text-4xl font-bold text-[#0F172A] tracking-tight mb-10">
            Answers for corporate procurement teams
          </h2>
          <div className="divide-y divide-[#E2E8F0] border-y border-[#E2E8F0]">
            {FAQS.map(({ q, a }, i) => (
              <details key={q} className="group py-6" data-testid={`metro-faq-${i}`} open={i < 3}>
                <summary className="flex items-start justify-between gap-6 cursor-pointer list-none">
                  <h3 className="text-base lg:text-lg font-semibold text-[#0F172A] group-hover:text-[#F97316] transition-colors" data-speakable="true">
                    {q}
                  </h3>
                  <span className="text-[#F97316] text-xl leading-none shrink-0 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-4 text-[#475569] leading-relaxed" data-speakable="true">{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ── POPULAR SEARCHES (long-tail internal linking + entity graph) ── */}
      <section className="py-14 lg:py-16 bg-white border-t border-[#E2E8F0]" data-testid="metro-popular-searches">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <span className="font-mono text-xs text-[#F97316] tracking-widest uppercase mb-3 block">Popular Searches</span>
          <h2 className="text-2xl lg:text-3xl font-bold text-[#0F172A] mb-8 tracking-tight max-w-3xl">
            Browse by product category, city or industry
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-xs font-mono uppercase tracking-widest text-[#64748B] mb-3">Product Categories</h3>
              <ul className="space-y-1.5 text-sm">
                {[
                  { s: "linear-workstations", t: "Modular Office Linear Workstations" },
                  { s: "corner-workstations", t: "Corner Workstations" },
                  { s: "acoustic-workstations", t: "Acoustic Workstations" },
                  { s: "height-adjustable-desks", t: "Height Adjustable Desks" },
                  { s: "executive-desks", t: "Executive Desks" },
                  { s: "meeting-tables", t: "Meeting & Boardroom Tables" },
                  { s: "phone-booths", t: "Acoustic Phone Booths" },
                  { s: "meeting-pods", t: "Meeting Pods" },
                  { s: "glass-partitions", t: "Glass Cabin Partitions" },
                  { s: "reception-counters", t: "Reception Counters" },
                ].map((p) => (
                  <li key={p.s}>
                    <Link to={`/products/${p.s}`} state={{ fromProducts: true }} className="text-[#0F172A] hover:text-[#F97316] underline underline-offset-4 decoration-transparent hover:decoration-[#F97316] transition-colors">
                      {p.t}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-xs font-mono uppercase tracking-widest text-[#64748B] mb-3">Cities Serviced</h3>
              <ul className="space-y-1.5 text-sm">
                {[
                  "Modular Office Furniture Manufacturer in Mumbai",
                  "Modular Office Furniture in Bengaluru",
                  "Corporate Office Furniture in Delhi NCR",
                  "Office Furniture Supplier in Pune",
                  "Modular Workstations in Hyderabad",
                  "Office Interior Setup in Chennai",
                  "Corporate Furniture in Kolkata",
                  "Office Fit-Out in Ahmedabad",
                ].map((c) => (
                  <li key={c}>
                    <Link to="/contact?intent=pan-india-quote" className="text-[#0F172A] hover:text-[#F97316] underline underline-offset-4 decoration-transparent hover:decoration-[#F97316] transition-colors">
                      {c}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-xs font-mono uppercase tracking-widest text-[#64748B] mb-3">By Industry</h3>
              <ul className="space-y-1.5 text-sm">
                {[
                  { s: "it-technology", t: "IT & Technology Offices" },
                  { s: "bfsi", t: "Banks & BFSI Workspaces" },
                  { s: "healthcare", t: "Healthcare Administration Offices" },
                  { s: "manufacturing", t: "Manufacturing Corporate Offices" },
                  { s: "consulting", t: "Consulting & Professional Services" },
                  { s: "startups", t: "Startups & Coworking" },
                  { s: "education", t: "Universities & Education Institutes" },
                  { s: "government", t: "Government & Public Sector" },
                ].map((i) => (
                  <li key={i.s}>
                    <Link to={`/solutions/${i.s}`} className="text-[#0F172A] hover:text-[#F97316] underline underline-offset-4 decoration-transparent hover:decoration-[#F97316] transition-colors">
                      {i.t}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <p className="text-xs text-[#94A3B8] mt-10 leading-relaxed max-w-4xl">
            Also known as: office furniture manufacturer india · commercial furniture supplier · modular workstation manufacturer · corporate office furniture factory · turnkey office interior contractor · B2B furniture manufacturer · direct factory office furniture · ISO certified furniture manufacturer · bulk office furniture supplier · acoustic pod manufacturer · phone booth supplier · conference table manufacturer india.
          </p>
        </div>
      </section>

      {/* ── CTA STRIP ─────────────────────────────────────────────── */}
      <section className="py-16 lg:py-20 bg-[#F97316] text-white" data-testid="metro-cta-strip">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24 flex flex-col lg:flex-row items-center justify-between gap-8">
          <div>
            <h2 className="text-2xl lg:text-3xl font-bold tracking-tight mb-2">Ready to source direct from the factory?</h2>
            <p className="text-white/90 text-sm lg:text-base">Share your floor plan — receive CAD layouts and quotation within 24-48 hours.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button
              asChild
              className="bg-[#0F172A] hover:bg-black text-white rounded-none px-8 py-6 font-semibold uppercase tracking-wider"
              data-testid="metro-final-cta-quote"
            >
              <Link to="/contact?intent=pan-india-quote">
                Request Project Quote
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
